export const DROPDOWN_WIDTH_PRESETS = {
  tiny: {
    uiMinWidthProperty: '--ui-dropdown-menu-tiny-min-width',
    uiMaxWidthProperty: '--ui-dropdown-menu-tiny-max-width',
    minWidthProperty: '--dropdown-tiny-menu-min-width',
    maxWidthProperty: '--dropdown-tiny-menu-max-width',
    fallbackWidth: 160,
  },
  compact: {
    uiMinWidthProperty: '--ui-dropdown-menu-compact-min-width',
    uiMaxWidthProperty: '--ui-dropdown-menu-compact-max-width',
    minWidthProperty: '--dropdown-compact-menu-min-width',
    maxWidthProperty: '--dropdown-compact-menu-max-width',
    fallbackWidth: 280,
  },
  default: {
    uiMinWidthProperty: '--ui-dropdown-menu-min-width',
    uiMaxWidthProperty: '--ui-dropdown-menu-max-width',
    minWidthProperty: '--dropdown-menu-min-width',
    maxWidthProperty: '--dropdown-menu-max-width',
    fallbackWidth: 320,
  },
};

/**
 * Menu HEIGHT tiers, keyed by `size` rather than by width preset.
 *
 * Only `size` selects a max-height in CSS (`primitives.css`: `.dropdown-menu` takes
 * `--ui-dropdown-menu-max-height`, `.dropdown-menu--compact` overrides it with the compact
 * token). Width presets never touch height, so reading the tier off `widthPreset` would
 * quietly return the wrong ceiling for any consumer that sets one.
 */
const DROPDOWN_HEIGHT_TIERS = {
  compact: {
    uiMaxHeightProperty: '--ui-dropdown-menu-compact-max-height',
    maxHeightProperty: '--dropdown-compact-menu-max-height',
    fallbackHeight: 260,
  },
  default: {
    uiMaxHeightProperty: '--ui-dropdown-menu-max-height',
    maxHeightProperty: '--dropdown-menu-max-height',
    fallbackHeight: 300,
  },
};

export function readCssPixelValue(value) {
  if (typeof value !== 'string') return 0;
  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

export function resolveDropdownWidthPreset(widthPreset, size = 'compact') {
  if (widthPreset && DROPDOWN_WIDTH_PRESETS[widthPreset]) {
    return widthPreset;
  }
  return size === 'default' ? 'default' : 'compact';
}

export function getDropdownWidthPresetClassName(widthPreset) {
  return `ui-floating-surface--width-${widthPreset}`;
}

export function getDropdownMenuWidthPresetClassName(widthPreset) {
  return `dropdown-menu--width-${widthPreset}`;
}

export function getDropdownWidthPresetMinWidth(widthPreset) {
  const presetConfig = DROPDOWN_WIDTH_PRESETS[widthPreset] ?? DROPDOWN_WIDTH_PRESETS.compact;
  return `var(${presetConfig.uiMinWidthProperty}, var(${presetConfig.minWidthProperty}))`;
}

export function getFallbackDropdownSurfaceWidth(widthPreset = 'compact', minWidth) {
  const rootStyle = typeof document !== 'undefined'
    ? window.getComputedStyle?.(document.documentElement)
    : null;
  const presetConfig = DROPDOWN_WIDTH_PRESETS[widthPreset] ?? DROPDOWN_WIDTH_PRESETS.compact;
  const explicitMinWidth = readCssPixelValue(minWidth);
  const themedMaxWidth = readCssPixelValue(rootStyle?.getPropertyValue(presetConfig.uiMaxWidthProperty))
    || readCssPixelValue(rootStyle?.getPropertyValue(presetConfig.maxWidthProperty));
  const themedMinWidth = readCssPixelValue(rootStyle?.getPropertyValue(presetConfig.uiMinWidthProperty))
    || readCssPixelValue(rootStyle?.getPropertyValue(presetConfig.minWidthProperty));
  if (explicitMinWidth) {
    return explicitMinWidth;
  }
  return themedMaxWidth || themedMinWidth || presetConfig.fallbackWidth;
}

/**
 * The tallest this menu could be, before it has been laid out and measured.
 *
 * The estimate the flip decision uses on the very first render, when there is no mounted
 * menu to measure yet. It reads the same tokens the stylesheet does, so a retuned
 * max-height moves the flip threshold with it instead of leaving a stale constant behind.
 * The literal fallbacks are for environments with no computed style at all (jsdom), which
 * is the same role `fallbackWidth` plays above -- not a styling value.
 */
export function getFallbackDropdownSurfaceHeight(size = 'compact', maxHeight) {
  const tier = DROPDOWN_HEIGHT_TIERS[size === 'default' ? 'default' : 'compact'];

  // An explicit maxHeight prop is the consumer overriding the tier outright, so it wins.
  const explicitMaxHeight = readCssPixelValue(maxHeight);
  if (explicitMaxHeight) return explicitMaxHeight;

  const rootStyle = typeof document !== 'undefined'
    ? window.getComputedStyle?.(document.documentElement)
    : null;
  const themedMaxHeight = readCssPixelValue(rootStyle?.getPropertyValue(tier.uiMaxHeightProperty))
    || readCssPixelValue(rootStyle?.getPropertyValue(tier.maxHeightProperty));
  return themedMaxHeight || tier.fallbackHeight;
}

/**
 * The gap the portal classes put between trigger and menu, so "fits" means fits WITH it.
 */
export function getDropdownMenuGap() {
  const rootStyle = typeof document !== 'undefined'
    ? window.getComputedStyle?.(document.documentElement)
    : null;
  return readCssPixelValue(rootStyle?.getPropertyValue('--dropdown-menu-gap')) || 8;
}
